import { useCallback, useEffect, useMemo, useState } from "react";

import debounce from "@mui/utils/debounce";
import { DateTimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";

export interface DateRangeDateTimePickerProps {
    label: string;
    value: DateTime | null;
    error?: string;
    onChange: (dateTime: DateTime | null) => void;
}

export function DateRangeDateTimePicker(props: DateRangeDateTimePickerProps) {
    const debouncedOnChange = useMemo(
        () => debounce(props.onChange, 500),
        [props.onChange],
    );

    const [isDateTimePickerOpen, setIsDateTimePickerOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<DateTime | null>(
        props.value,
    );

    const onChangeDateTime = useCallback(
        (value: DateTime | null) => {
            setSelectedDate(value);

            if (!isDateTimePickerOpen) {
                debouncedOnChange(value);
            }
        },
        [debouncedOnChange, isDateTimePickerOpen],
    );

    useEffect(() => {
        setSelectedDate(props.value);
    }, [props.value]);

    return (
        <DateTimePicker
            label={props.label}
            value={selectedDate}
            views={["year", "month", "day", "hours", "minutes"]}
            disableFuture
            sx={{
                paddingBottom: 3,
            }}
            onOpen={() => setIsDateTimePickerOpen(true)}
            onClose={() => setIsDateTimePickerOpen(false)}
            onChange={onChangeDateTime}
            onAccept={(value) => debouncedOnChange(value)}
            slotProps={{
                textField: {
                    helperText: props.error,
                    error: !!props.error,
                },
            }}
        />
    );
}
